import { FC, useEffect, useRef, useState } from 'react';
import { BaseDocumentForm } from '../../../../components/documents/baseDocumentForm';
import { DefaultGrid } from '../../../../components/grids/default/defaultGrid';
import { Spinner } from '../../../../components/spiner/Spinner';
import { IActRejectionDTO, IActRejectionViewDTO } from '../../../../libs/coreapi-dto/documents/ActRejection';
import { ActRejectionDataProvider } from '../../../../Services/DataProviders/ActRejectionDataProvider';
import useGridFilter, { DefaultGridFilter } from '../../../../system/hooks/useGridFilter';
import { useAppContext } from '../../../../system/providers/appContextProvider';
import { usePluginContext } from '../../../../system/providers/plugin';
import styles from '../styles/ActRejection.module.scss';
import { DocumentType, IdTableVariant } from "../../../../@types/enumsGlobal";
import GridWrapper from '../../../../components/controls/GridWrapper';
import { DateInput, TextInput } from '../../../../components/controls/inputs/BaseInput';
import { t } from 'i18next';
import { CreateVariant } from '../../../MasterTabsProvider';
import { useTabsContext } from '../../../../system/providers/tabsProvider';
import { IKeyValuePair } from '../../../Reports/Shared/Interfaces/IKeyValuePair';
import { ExportExcelWithHeader } from '../exportExel/exportExcel';
import { DateTime } from 'luxon';

export interface IActRejectionItemViewProps {
    gridId: string;
    plugin: IPluginSettings;
    idGlobal: string;
    cancel?: () => void;
    variant: CreateVariant | undefined,
    data?: any
    parentCallback?: <T>(message: T) => void,
}

type IActRejectionExcel = Record<string, string | number>;

const ActRejectionItem: FC<IActRejectionItemViewProps> = (props) => {
    const appContext = useAppContext();
    const pluginCtx = usePluginContext();
    const tabsContext = useTabsContext();

    const actRejectionDP = new ActRejectionDataProvider(appContext.coreApiService);
    const [dataItem, setDataItem] = useState<IActRejectionDTO[]>([]);
    const [data, setData] = useState<IActRejectionViewDTO[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultGridFilter);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();

    const actionButtonsRef = useRef<HTMLDivElement>(null);
    const additionalActionsRef = useRef<HTMLDivElement>(null);
    
    const loadData = () => {
        if (!props.idGlobal) {
            setIsSubmitting(false);
            return;
        }

        setIsSubmitting(true);

        actRejectionDP.getItemById(props.idGlobal, gridFilter, (data, totalCount) => {
            setDataItem(data);
            setTotalCount(totalCount);
            setIsSubmitting(false);
        });

        const filterActRejection = {
            numberPerPage: 1,
            pageNumber: 1,
            columnFilters: [
                {
                    name: "idGlobal",
                    operator: 'Eq',
                    value: props.idGlobal
                }
            ],
        } as IGridFilter;

        actRejectionDP.getView(filterActRejection, (data) => {
            setData(data);
            setSelectedItem(undefined);
        });
    };

    useEffect(() => {
        loadData();
    }, [props.idGlobal]);

    useEffect(() => {
        if (props.idGlobal) {
            loadData();
        }
    }, [props.idGlobal]);

    const handleCancel = () => {
        if (props.cancel) {
            props.cancel();
        } else {
            const currentTab = tabsContext.currentTab;
            if (currentTab) {
                tabsContext.close(currentTab);
            }
        }
    };

    const exportExcel = (entities: IActRejectionDTO[]) => {
        const headerData = data[0] || {};

        const excelData: IActRejectionExcel[] = entities.map((item) => ({
            [t("documents.actRejection.fields.goodsName")]: item.goodsName || '',
            [t("documents.actRejection.fields.producerName")]: item.producerName || '',
            [t("documents.actRejection.fields.seriesNumber")]: item.seriesNumber || '',
            [t("documents.actRejection.fields.quantityRem")]: item.quantityRem || 0,
            [t("documents.actRejection.fields.supplierName")]: item.supplierName || '',
            [t("documents.actRejection.fields.invoiceNum")]: item.invoiceNum || '',
            [t("documents.actRejection.fields.falsificatDocNum")]: item.falsificatDocNum || '',
            [t("documents.actRejection.fields.falsificatDocDate")]: item.falsificatDocDate?.toFormat('dd.MM.yyyy') || '',
            [t("documents.actRejection.fields.falsificatReasons")]: item.falsificatReasons || '',
            [t("documents.actRejection.fields.falsificatLabName")]: item.falsificatLabName || '',
        }));

        const headerInfo = {
            [t("documents.actRejection.fields.contractorName")]: headerData.contractorName || '',
            [t("documents.actRejection.fields.storeName")]: headerData.storeName || '',
            [t("documents.actRejection.fields.mnemocode")]: headerData.mnemocode || '',
            [t("documents.actRejection.fields.documentDate")]: headerData.documentDate?.toFormat('dd.MM.yyyy') || '',
        };

        const fileName = `Акт_о_забраковке_${headerData.mnemocode || 'документ'}_${DateTime.now().toFormat('dd.MM.yyyy')}`;

        ExportExcelWithHeader(excelData, headerInfo, fileName);
    };

    const handleExport = async () => {
        if (isExporting) return;

        setIsExporting(true);

        try {
            const filterForExport = {
                ...gridFilter,
                numberPerPage: 10000,
                pageNumber: 1,
                columnFilters: []
            } as IGridFilter;

            actRejectionDP.getItemById(props.idGlobal, filterForExport, (entities) => {
                if (!entities || entities.length === 0) {
                    setIsExporting(false);
                    return;
                }

                exportExcel(entities);
                setIsExporting(false);
            });
        } catch (error) {
            console.error('Export error:', error);
            setIsExporting(false);
        }
    };

    return (
        <BaseDocumentForm
            cancel={{
                onClick: handleCancel,
                title: t("general.cancel"),
                tabIndex: 1,
            }}
            otherAction={{
                onClick: handleExport,
                title: isExporting ? t("general.exporting") : t("general.export"),
                disabled: isSubmitting || !data.length,
                sendRequestSpinner: isExporting,
            }}
            // print={{
            //     onClick: () => {
            //         return IdTableVariant.RejectionAct;
            //     },
            //     title: t("general.print"),
            //     onSubItemClick: () => {
            //         let arr = new Array<IKeyValuePair>();
            //         if (props.data != undefined && props.data.mnemocode != undefined && props.idGlobal != undefined)
            //             arr.push({ key: props.idGlobal, value: props.data.mnemocode });

            //         return arr;
            //     },
            // }}
            actionButtonsRef={actionButtonsRef}
            additionalActionsRef={additionalActionsRef}
            documentFormClassName={styles.documentForm}
        >
            <div className={styles.container}>
                <div className={styles.wrapper}>
                    <GridWrapper cols={4}>
                        <TextInput
                            label={t("documents.actRejection.contractorName")}
                            inputId={"inputActRejectionContractor"}
                            value={data[0]?.contractorName}
                            inline
                            disabled
                        />
                        <TextInput
                            label={t("documents.actRejection.storeName")}
                            inputId={"inputActRejectionStore"}
                            value={data[0]?.storeName}
                            inline
                            disabled
                        />
                        <TextInput
                            label={t("documents.actRejection.number")}
                            inputId={"inputActRejectionNumber"}
                            value={data[0]?.mnemocode}
                            inline
                            disabled
                        />
                        <DateInput
                            required
                            labelVariant="required"
                            inputId={"inputActRejectionDate"}
                            label={t("documents.invoice.date")}
                            value={data[0]?.dateCreated}
                            inline
                            disabled={true}
                        />
                    </GridWrapper>
                </div>
            </div>
            <div className={styles.gridWrapper}>
                <div className={styles.masterGrid}>
                    {isSubmitting ? (
                        <Spinner />
                    ) : (
                        <>
                            {totalCount > 0 ? (
                                <div className={styles.titleQuantityFalsification}>{t("documents.actRejection.quantityFalsification")} {totalCount}</div>
                            ) : (
                                <div className={styles.titleQuantityFalsification}>{t("documents.actRejection.noQuantityFalsification")}</div>
                            )}
                            <DefaultGrid
                                separator
                                gridId={props.gridId}
                                data={dataItem}
                                filter={gridFilter}
                                dataProvider={actRejectionDP}
                                totalCount={totalCount}
                                plugin={props.plugin}
                                selectedItem={selectedItem}
                                isServerFilter
                                searching
                                getView={(gridFilter, callback) => {
                                    if (!props.idGlobal) return
                                    actRejectionDP.getItemById(props.idGlobal, gridFilter, (entities, totalCount) => {
                                        callback(entities, totalCount)
                                        setSelectedItem(undefined);
                                    })
                                }}
                                onSelect={(row) => {
                                    setSelectedItem(row);
                                    row ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.actRejection) : pluginCtx.masterGrid.onUnselectEvent();
                                }}
                                onSort={(i) => {
                                    dispatchGridFilter({ type: "sort", payload: i.propertyName });
                                }}
                                onFilterDelete={(i) => {
                                    dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName });
                                }}
                                onPageNumberChange={(n) => {
                                    dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } });
                                }}
                                onNumberPerPageChange={(n) => {
                                    dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } });
                                }}
                            />
                        </>
                    )}
                </div>
            </div>
        </BaseDocumentForm>
    )
};

export default ActRejectionItem;